import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { APP_INITIALIZER, EnvironmentProviders, InjectionToken, makeEnvironmentProviders } from "@angular/core";
import {
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
  MSAL_BROADCAST_CONFIG,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG
} from "@azure/msal-angular";
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication
} from "@azure/msal-browser";
import { ConfigService } from "./config.service";

const AUTH_CONFIG_URL_TOKEN = new InjectionToken<string>('AUTH_CONFIG_URL');

let initializedMsalInstance: IPublicClientApplication | null = null;

export function initializerFactory(env: ConfigService, configUrl: string): any {
  const promise = env.init(configUrl)
    .then((value) => {
      const msalInstance = new PublicClientApplication({
        auth: {
          clientId: env.get().ClientId,
          authority: new URL(env.get().TenantId, env.get().Instance).toString(),
          redirectUri: env.get().RedirectUri ?? window.location.origin,
        },
        cache: {
          cacheLocation: BrowserCacheLocation.LocalStorage
        },
        system: {
          loggerOptions: {
            loggerCallback,
            logLevel: LogLevel.Error,
            piiLoggingEnabled: false
          }
        }
      });

      return msalInstance.initialize().then(() => {
        initializedMsalInstance = msalInstance;
        return msalInstance.handleRedirectPromise().then(() => msalInstance);
      });
    });
  return () => promise;
}

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(config: ConfigService): IPublicClientApplication {
  if (initializedMsalInstance) {
    return initializedMsalInstance;
  }

  throw new Error('MSAL instance not initialized.');
}

export function MSALInterceptorConfigFactory(config: ConfigService): MsalInterceptorConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: new Map([
      ['https://graph.microsoft.com/v1.0/me', ['user.read']],
      ['/api/*', [`api://${config.get().ClientId}/scope`]],
      ['/jobs/*', [`api://${config.get().ClientId}/scope`]]
    ]),
    authRequest: (authService, request) => ({
      state: window.location.origin
    })
  };
}

export function MSALGuardConfigFactory(config: ConfigService): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: (authService, state) => ({
      state: window.location.origin
    })
  };
}

export function provideMsal(configFile: string): EnvironmentProviders {
  return makeEnvironmentProviders([
    ConfigService,
    { provide: AUTH_CONFIG_URL_TOKEN, useValue: configFile },
    {
      provide: APP_INITIALIZER,
      useFactory: initializerFactory,
      deps: [ConfigService, AUTH_CONFIG_URL_TOKEN],
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [ConfigService]
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
      deps: [ConfigService]
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
      deps: [ConfigService]
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_BROADCAST_CONFIG,
      useValue: {
        eventsToReplay: 5
      }
    }
  ]);
}
